package raw.engine



import raw.models.*
import kotlin.math.floor

class RewardStrategyEngine {

    public fun evaluate(
        transaction: Transaction,
        inventory: List<CreditCard>,
        availableRoutes: List<RouteDefinition>,
        userState: UserState
    ): List<CalculationResult> {

        println("DEBUG: User wallet has ${userState.ownedCards.size} cards.")
        val isOnlineChannel = transaction.channel.equals("ONLINE", ignoreCase = true)

        // 1. Context-Aware Filtering (Offline vs Portal)
        val validRoutes = if (isOnlineChannel) {
            availableRoutes
        } else {
            availableRoutes.filter { it.type != "PORTAL" }
        }

        val cardMap = inventory.associateBy { it.id }

        // 2. 🟢 THE FIX: Iterate over the user's actual wallet, not the whole inventory
        val allPossiblePaths = userState.ownedCards.flatMap { ownedCard ->
            val cardTemplate = cardMap[ownedCard.id]

            if (cardTemplate != null) {
                // 🟢 THE FIX: The Bouncer is gone.
                // Send every card to generatePaths. The generator will handle creating
                // the UPI route ONLY if ownedCard.network == "RuPay".
                generatePaths(cardTemplate, transaction, validRoutes, ownedCard.network)
            } else {
                emptyList()
            }
        }

        // 4. Simulate the valid paths
        val results = allPossiblePaths.mapNotNull { path ->
            val cardTemplate = cardMap[path.cardId]
            val ownedCard = userState.ownedCards.find { it.id == path.cardId }
            if (cardTemplate != null) {
                simulatePath(path, cardTemplate, transaction, ownedCard)
            } else {
                null
            }

        }

        println("DEBUG: Generated ${allPossiblePaths.size} total paths before simulation.")

        // 5. 🟢 THE NEW SORT: Use the maxYieldInr calculated from the Valuation Spectrum
        return results.sortedByDescending { it.maxYieldInr }
    }

    private fun generatePaths(
        card: CreditCard,
        txn: Transaction,
        routes: List<RouteDefinition>,
        userNetwork: String? // 🟢 ADDED: To support the Network Bouncer
    ): List<RewardPath> {
        val validPaths = mutableListOf<RewardPath>()
        val rules = card.rules

        // ==========================================
        // 1. DIRECT PATH (Always Check)
        // ==========================================
        // 🟢 THE FIX: Pass userNetwork down so the Bouncer can intercept invalid UPI networks
        val directRuleResolution = resolveDirectSwipeRule(txn, card, userNetwork)

        validPaths.add(
            RewardPath(
                id = "${card.id}_DIRECT",
                cardId = card.id,
                route = RewardRoute.Direct,
                rule = directRuleResolution.rule, // Can be null if no fallback exists
                receiptPrefix = directRuleResolution.receiptPrefix, // e.g., "Rule: Category Excluded"
                isPoisoned = directRuleResolution.isExcluded // Flag to force 0 points
            )
        )

        // ==========================================
        // 2. UPI PATH (Only Evaluated for RuPay)
        // ==========================================
        if (userNetwork.equals("RuPay", ignoreCase = true)) {
            val upiRuleResolution = resolveUpiRule(txn, card)

            validPaths.add(
                RewardPath(
                    id = "${card.id}_UPI",
                    cardId = card.id,
                    route = RewardRoute.Upi,
                    rule = upiRuleResolution.rule,
                    receiptPrefix = upiRuleResolution.receiptPrefix,
                    isPoisoned = upiRuleResolution.isExcluded
                )
            )
        }

        // ==========================================
        // 3. DYNAMIC ROUTES (Portals/Vouchers)
        // ==========================================
        val routeRules = rules.filter { it.matcher.type == "ROUTE_ID" }

        for (rule in routeRules) {

            // 🟢 THE BOUNCER: Network check for Route Rules
            if (rule.allowedNetworks != null && userNetwork != null) {
                if (!rule.allowedNetworks.contains(userNetwork)) continue
            }

            // 🟢 THE BOUNCER: Channel check for Route Rules
            if (rule.channelRequirement != null && !rule.channelRequirement.equals(txn.channel, ignoreCase = true)) {
                continue
            }

            val requiredRouteId = rule.matcher.value ?: continue
            val routeDef = routes.find { it.id == requiredRouteId } ?: continue

            // 🟢 THE FIX: Replaced txn.isOnline with the new channel architecture
            if (routeDef.type == "PORTAL" && !txn.channel.equals("ONLINE", ignoreCase = true)) {
                continue
            }

            // --- FILTER 1: MERCHANT SUPPORT CHECK ---
            if (routeDef.merchantConstraints != null) {
                val constraints = routeDef.merchantConstraints
                // 🟢 THE FIX: Using txn.categoryId instead of txn.category
                val isCategoryMatch = constraints.allowedCategories.isEmpty() ||
                        constraints.allowedCategories.contains(txn.categoryId)
                val isMerchantWhitelisted = constraints.allowedMerchants.isNotEmpty() &&
                        txn.merchantId != null &&
                        constraints.allowedMerchants.contains(txn.merchantId)

                if (!isCategoryMatch && !isMerchantWhitelisted) {
                    continue
                }
            }

            // --- FILTER 2: SUB-CATEGORY CHECK ---
            if (!rule.matcher.subCategories.isNullOrEmpty()) {
                // 🟢 THE FIX: Using txn.categoryId
                val categoryName = txn.categoryId.uppercase()
                val match = rule.matcher.subCategories.any { sub ->
                    categoryName.contains(sub, ignoreCase = true)
                }
                if (!match) continue
            }

            val routeInstance = when (routeDef.type) {
                "PORTAL" -> RewardRoute.Portal(routeDef.id, routeDef.name)
                "VOUCHER_AGGREGATOR" -> RewardRoute.Voucher(routeDef.id,"Voucher", routeDef.id, txn.merchantName ?: "Unknown",routeDef.description,routeDef.link)
                "VOUCHER" -> RewardRoute.Voucher(routeDef.id,"Voucher", routeDef.id, txn.merchantName ?: "Unknown", routeDef.description,routeDef.link)
                "APP_PAYMENT" -> RewardRoute.AppPayment(routeDef.id, routeDef.name, routeDef.description,routeDef.link)
                "WALLET" -> RewardRoute.Portal(routeDef.id, routeDef.name)
                else -> continue
            }

            validPaths.add(
                RewardPath(
                    id = "${card.id}_${requiredRouteId}_${rule.id}",
                    cardId = card.id,
                    route = routeInstance,
                    rule = rule // Pass the entire rule object
                )
            )
        }

        return validPaths
    }


    private fun simulatePath(
        path: RewardPath,
        card: CreditCard,
        txn: Transaction,
        ownedCard: OwnedCard?,
    ): CalculationResult? {

        val currencyName = card.rewardCurrency.name

        // ==========================================
        // 1. POISON PILL INTERCEPTOR
        // ==========================================
        if (path.isPoisoned || path.rule == null) {
            val fallbackText = path.receiptPrefix.ifEmpty { "Rule: No Eligible Rule Found" }
            return CalculationResult(
                cardId = card.id,
                cardName = card.name,
                routeName = path.route.name,
                routeType = "DIRECT",
                rawPointsEarned = 0,
                basePoints = 0.0,
                bonusPoints = 0.0,
                maxYieldInr = 0.0,
                mathBreakdown = "$fallbackText\nEarned: 0 $currencyName", // 🟢 Formatted with newline
                isCapped = false,
                spectrumOptions = emptyList(),
                currencyName = currencyName
            )
        }

        val rule = path.rule

        // ==========================================
        // 2. BASE POINTS CALCULATION (Math Only)
        // ==========================================
        val baseRule = card.rules.firstOrNull { it.matcher.values?.contains("ALL") == true }
        var basePoints = 0.0

        if (baseRule != null) {
            if (baseRule.rewardBlock != null) {
                val blocks = floor(txn.amount / baseRule.rewardBlock.spendRequired).toInt()
                basePoints = (blocks * baseRule.rewardBlock.pointsAwarded).toDouble() // 🟢 Math calculated correctly
            } else if (baseRule.rate != null) {
                basePoints = floor(txn.amount * baseRule.rate)
            }
        }

        // ==========================================
        // 3. RAW TOTAL EARN CALCULATION
        // ==========================================
        var rawPointsEarned = 0.0

        if (rule.rewardBlock != null) {
            val totalBlocks = floor(txn.amount / rule.rewardBlock.spendRequired).toInt()
            val basePts = rule.rewardBlock.pointsAwarded
            val multiplier = rule.acceleratedMultiplier ?: 1.0
            rawPointsEarned = totalBlocks * (basePts * multiplier)
        } else if (rule.rate != null) {
            rawPointsEarned = floor(txn.amount * rule.rate)
        } else {
            return null
        }

        // ==========================================
        // 4. APPLY CAPS
        // ==========================================
        var isCapped = false
        var finalPointsEarned = rawPointsEarned

        val activeCapRef = rule.capRef
        if (activeCapRef != null && card.caps != null) {
            val activeCapRule = card.caps[activeCapRef]

            if (activeCapRule != null) {
                val used = ownedCard?.capUsage?.get(activeCapRef) ?: 0.0
                val limit = activeCapRule.limit

                val available = maxOf(0.0, limit - used)

                if (rawPointsEarned > available) {
                    finalPointsEarned = available
                    isCapped = true
                }
            }
        }

        // ==========================================
        // 5. BASE & BONUS DATA RE-BALANCING
        // ==========================================
        val isFlatRate = baseRule == null || baseRule.id == rule.id

        val exportedBasePoints: Double
        val exportedBonusPoints: Double

        if (isFlatRate) {
            // SCENARIO A: Flat Rate (SBI). All yield is Base. Zero Bonus.
            exportedBasePoints = finalPointsEarned
            exportedBonusPoints = 0.0
        } else {
            // SCENARIO B: Split Rate (Axis Atlas).
            exportedBasePoints = basePoints
            exportedBonusPoints = maxOf(0.0, finalPointsEarned - basePoints)
        }

        // ==========================================
        // 6. VALUATION SPECTRUM & MAX YIELD
        // ==========================================
        val spectrumEvaluated = card.valuationSpectrum.map {spectrum->

            val formattedRate = if (spectrum.valueInr % 1.0 == 0.0) {
                spectrum.valueInr.toInt().toString()
            } else {
                spectrum.valueInr.toString()
            }

            // 🟢 Build the human-readable logic string
            val logicString = "1 $currencyName = ₹$formattedRate"

            SpectrumResult(
                label = spectrum.label,
                liquidity = spectrum.liquidity,
                inrValue = finalPointsEarned * spectrum.valueInr,
                redemptionLogic = logicString
            )
        }.sortedByDescending { it.inrValue }

        val maxYield = spectrumEvaluated.firstOrNull()?.inrValue ?: 0.0

        // ==========================================
        // 7. STRUCTURED RECEIPT GENERATION (UI Formatting)
        // ==========================================
        val breakdownLines = mutableListOf<String>()

        val formatPct = { rate: Double ->
            val pct = rate * 100
            if (pct % 1.0 == 0.0) "${pct.toInt()}%" else "$pct%"
        }

        if (isFlatRate) {
            // 🟢 Flat Rate Receipt (Single Line)
            val currentBlock = rule.rewardBlock
            val currentRate = rule.rate

            if (currentBlock != null) {
                val blocks = floor(txn.amount / currentBlock.spendRequired).toInt()
                breakdownLines.add("Base: ₹${txn.amount.toInt()} ÷ ₹${currentBlock.spendRequired} = $blocks blocks. $blocks × ${currentBlock.pointsAwarded} pts = ${finalPointsEarned.toInt()} $currencyName")
            } else if (currentRate != null) {
                breakdownLines.add("Base: ₹${txn.amount.toInt()} × ${formatPct(currentRate)} = ${finalPointsEarned.toInt()} $currencyName")
            }
        } else {
            // 🟢 Split Rate Receipt (Base Line + Bonus Line)
            val baseBlock = baseRule?.rewardBlock
            val baseRate = baseRule?.rate

            if (baseBlock != null) {
                val blocks = floor(txn.amount / baseBlock.spendRequired).toInt()
                breakdownLines.add("Base: ₹${txn.amount.toInt()} ÷ ₹${baseBlock.spendRequired} = $blocks blocks. $blocks × ${baseBlock.pointsAwarded} pts = ${exportedBasePoints.toInt()} $currencyName")
            } else if (baseRate != null) {
                breakdownLines.add("Base: ₹${txn.amount.toInt()} × ${formatPct(baseRate)} = ${exportedBasePoints.toInt()} $currencyName")
            }

            if (exportedBonusPoints > 0) {
                val ruleName = rule.name ?: path.receiptPrefix.ifEmpty { "Bonus Rule" }
                val multiplierTxt = rule.acceleratedMultiplier?.let { " (${it.toInt()}X Accelerated)" } ?: ""
                breakdownLines.add("Bonus: +${exportedBonusPoints.toInt()} $currencyName via $ruleName$multiplierTxt")
            }
        }

        if (isCapped) {
            breakdownLines.add("⚠️ Cap Hit: Monthly limit reached. Total reduced to ${finalPointsEarned.toInt()} $currencyName")
        }

        val mathBreakdown = breakdownLines.joinToString("\n")

        // ==========================================
        // 8. DETERMINE ROUTE TYPE
        // ==========================================
        val routeTypeStr = when (path.route) {
            is RewardRoute.Direct -> "DIRECT"
            is RewardRoute.Portal -> "PORTAL"
            is RewardRoute.Voucher -> "VOUCHER"
            is RewardRoute.AppPayment -> "APP_PAYMENT"
            is RewardRoute.Wallet -> "WALLET"
            is RewardRoute.Upi -> "UPI"
        }

        // ==========================================
        // 9. RETURN FRONTEND PAYLOAD
        // ==========================================
        return CalculationResult(
            cardId = card.id,
            cardName = card.name,
            routeName = if (path.route is RewardRoute.Direct) "Direct Swipe" else path.route.name,
            routeType = routeTypeStr,
            rawPointsEarned = finalPointsEarned.toInt(),
            basePoints = exportedBasePoints, // 🟢 NOW EXPORTING RE-BALANCED BUCKETS
            bonusPoints = exportedBonusPoints, // 🟢 NOW EXPORTING RE-BALANCED BUCKETS
            maxYieldInr = maxYield,
            mathBreakdown = mathBreakdown,
            isCapped = isCapped,
            spectrumOptions = spectrumEvaluated,
            currencyName = currencyName,
            routeDescription = path.route.description,
            routeLink = path.route.link
        )
    }

    private fun resolveDirectSwipeRule(
        txn: Transaction,
        card: CreditCard,
        userNetwork: String?
    ): DirectResolution {

        val channelStr = txn.channel.uppercase()

        // Helper to validate network and channel requirements
        fun isRuleValid(rule: CardRule): Boolean {
            // 1. Network Bouncer
            if (rule.allowedNetworks != null && userNetwork != null) {
                if (!rule.allowedNetworks.contains(userNetwork)) return false
            }
            // 2. Channel Bouncer
            if (rule.channelRequirement != null && !rule.channelRequirement.equals(channelStr, ignoreCase = true)) {
                return false
            }
            return true
        }

        // STEP 1: MERCHANT MATCH
        if (txn.merchantId != null) {
            val merchantRule = card.rules.firstOrNull { rule ->
                rule.matcher.type == "MERCHANT_ID" &&
                        rule.matcher.values?.map { it.uppercase() }?.contains(txn.merchantId!!.uppercase()) == true &&
                        isRuleValid(rule)
            }
            if (merchantRule != null) return DirectResolution(merchantRule, merchantRule.name ?: "Specific Merchant", false)
        }

        // STEP 2: CATEGORY EXCLUSION (The Poison Pill)
        val isExcluded = card.exclusions.any { exclusion ->
            exclusion.type == "CATEGORY" && exclusion.values.contains(txn.categoryId)
        }
        if (isExcluded) return DirectResolution(null, "Category Excluded", true)

        // STEP 3: CATEGORY + CHANNEL MATCH
        val categoryRule = card.rules.firstOrNull { rule ->
            val isCatMatch = rule.matcher.type == "CATEGORY" && rule.matcher.values?.contains(txn.categoryId) == true
            val isPureChannelMatch = rule.matcher.type == "CHANNEL" && rule.matcher.values?.contains(channelStr) == true

            (isCatMatch || isPureChannelMatch) && isRuleValid(rule)
        }
        if (categoryRule != null) return DirectResolution(categoryRule, categoryRule.name ?: "Category/Channel Match", false)

        // STEP 4: BASE RATE FALLBACK
        val baseRule = card.rules.firstOrNull { rule ->
            rule.matcher.type == "CATEGORY" && rule.matcher.values?.contains("ALL") == true && isRuleValid(rule)
        }
        return DirectResolution(baseRule, baseRule?.name ?: "Base Rate", false)
    }

    // Helper data class (if you don't already have it)
    data class RuleResolution(
        val rule: CardRule?,
        val isExcluded: Boolean = false,
        val receiptPrefix: String = ""
    )

    private fun resolveUpiRule(txn: Transaction, card: CreditCard): RuleResolution {
        // 1. Check absolute exclusions (e.g., Fuel or Rent via UPI)
        val triggeredExclusion = card.exclusions.find { exclusion ->
            when (exclusion.type.uppercase()) {
                "CATEGORY" -> exclusion.values.contains(txn.categoryId)
                "MERCHANT_ID" -> txn.merchantId != null && exclusion.values.contains(txn.merchantId)
                else -> false
            }
        }

        if (triggeredExclusion != null) {
            return RuleResolution(
                rule = null,
                isExcluded = true,
                // Fallback message if you haven't defined a custom one in JSON
                receiptPrefix = triggeredExclusion.message ?: "Excluded ${triggeredExclusion.type.lowercase().replace("_", " ")}"
            )
        }

        // 2. Find the specific UPI Rule in cards.json
        val upiRule = card.rules.find {
            it.matcher.type == "PAYMENT_METHOD" && it.matcher.values?.contains("UPI") == true
        }

        if (upiRule != null) {
            return RuleResolution(rule = upiRule)
        }

        // 3. Fallback: If a RuPay card doesn't have a specific UPI rule, it earns 0
        return RuleResolution(
            rule = null,
            isExcluded = true,
            receiptPrefix = "No UPI Rewards Configured"
        )
    }

    fun RouteDefinition.toRewardRoute(): RewardRoute {
        return when (this.type) {
            "PORTAL" -> RewardRoute.Portal(
                id = this.id,
                name = this.name,
                description = this.description, // 🟢 Metadata Passthrough
                link = this.link                // 🟢 Metadata Passthrough
            )
            "VOUCHER" -> RewardRoute.Voucher(
                id = this.id,
                name = this.name,
                description = this.description, // 🟢 Metadata Passthrough
                link = this.link,
                aggregator = "",
                brand = ""                // 🟢 Metadata Passthrough
            )
            "APP_PAYMENT" -> RewardRoute.AppPayment(
                id = this.id,
                name = this.name,
                description = this.description,
                link = this.link
            )
            "WALLET" -> RewardRoute.Wallet(
                id = this.id,
                name = this.name,
                description = this.description,
                link = this.link
            )
            else -> throw IllegalArgumentException("Unknown route type: ${this.type}")
        }
    }

//    private fun matchesRule(
//        type: String,
//        singleValue: String?,
//        multiValues: List<String>?,
//        txn: Transaction
//    ): Boolean {
//        val targetValues = (multiValues ?: listOfNotNull(singleValue)).map { it.uppercase() }
//
//        return when (type) {
//            "CATEGORY" -> {
//                val cleanCategory = txn.categoryId.removePrefix("cat_").uppercase()
//                targetValues.any { ruleCat ->
//                    ruleCat == cleanCategory || ruleCat == txn.categoryId.uppercase() || cleanCategory.startsWith(ruleCat + "_")
//                }
//            }
//            "MERCHANT_ID" -> {
//                val txnMerchantId = txn.merchantId?.uppercase()
//                txnMerchantId != null && targetValues.contains(txnMerchantId)
//            }
////            "MCC" -> targetValues.contains(txn.mcc)
//            "MERCHANT_NAME" -> targetValues.any { txn.merchantName.uppercase().contains(it) }
//            "CHANNEL" -> if (targetValues.contains("ONLINE")) txn.isOnline else !txn.isOnline
//            else -> false
//        }
//    }
}